import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ShieldAlert, Shield, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Security Audit Log — admin-only view of every blocked upload, prompt-
 * injection detection, and rate-limit hit. Fed by
 * GET /api/admin/security-events, which returns:
 *   { events: [...], counts_by_kind: {...}, total: N }
 *
 * We render two things:
 *   (1) A rollup strip — one card per event `kind`, sorted by frequency.
 *   (2) A most-recent-N event table with actor + reason + details.
 */

// Human labels for the internal `kind` slugs. Anything not in the map falls
// back to a title-cased version of the slug.
const KIND_LABELS = {
  upload_blocked_executable: 'Executable blocked',
  upload_blocked_macro: 'Macro doc blocked',
  upload_blocked_extension: 'Extension blocked',
  upload_blocked_size: 'Oversize / empty',
  upload_blocked_content: 'Content-magic mismatch',
  upload_blocked_hash: 'Known-malware hash',
  upload_blocked_clamav: 'ClamAV FOUND',
  upload_rate_limited: 'Rate limited',
  prompt_injection_neutralized: 'Prompt injection',
  pii_redacted: 'PII redacted',
};

const humanize = (slug) =>
  KIND_LABELS[slug] ||
  slug
    .replace(/_/g, ' ')
    .replace(/(^|\s)\S/g, (l) => l.toUpperCase());

const SecurityEventsPanel = ({ adminSecret }) => {
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterKind, setFilterKind] = useState('');

  const fetchEvents = useCallback(async () => {
    if (!adminSecret) return;
    setLoading(true);
    try {
      const params = { admin_secret: adminSecret, limit: 100 };
      if (filterKind) params.kind = filterKind;
      const res = await axios.get(`${API}/admin/security-events`, { params });
      setEvents(res.data.events || []);
      setCounts(res.data.counts_by_kind || {});
    } catch (e) {
      // Silent — the outer AdminPanel already surfaces auth errors.
      if (process.env.NODE_ENV !== 'production') console.debug('security-events fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [adminSecret, filterKind]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const sortedKinds = Object.entries(counts).sort((a, b) => b[1] - a[1]);

  return (
    <div
      data-testid="security-events-panel"
      className="rounded-lg border border-white/10 bg-zinc-900/40 backdrop-blur-sm p-6 mt-8"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-400" aria-hidden />
          <h2 className="text-xl font-serif text-zinc-100">Security Audit</h2>
        </div>
        <button
          data-testid="security-refresh-button"
          onClick={fetchEvents}
          disabled={loading}
          className="text-zinc-400 hover:text-zinc-200 transition-colors text-sm flex items-center gap-1.5 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Rollup strip */}
      {sortedKinds.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <button
            onClick={() => setFilterKind('')}
            data-testid="security-filter-all"
            className={`text-left rounded-md border p-3 transition-colors ${
              filterKind === ''
                ? 'border-amber-500/40 bg-amber-500/10'
                : 'border-white/10 bg-black/20 hover:border-white/20'
            }`}
          >
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">All events</div>
            <div className="text-2xl font-mono text-zinc-100">
              {Object.values(counts).reduce((a, b) => a + b, 0)}
            </div>
          </button>
          {sortedKinds.map(([kind, n]) => (
            <button
              key={kind}
              onClick={() => setFilterKind(kind === filterKind ? '' : kind)}
              data-testid={`security-filter-${kind}`}
              className={`text-left rounded-md border p-3 transition-colors ${
                filterKind === kind
                  ? 'border-amber-500/40 bg-amber-500/10'
                  : 'border-white/10 bg-black/20 hover:border-white/20'
              }`}
            >
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
                {humanize(kind)}
              </div>
              <div className="text-2xl font-mono text-zinc-100">{n}</div>
            </button>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-2 mb-4 text-zinc-500 text-sm">
          <Shield className="w-4 h-4 text-emerald-400" aria-hidden />
          No security events recorded — the platform is clean.
        </div>
      )}

      {/* Event list */}
      {events.length > 0 && (
        <div className="rounded-md border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/30">
              <tr className="text-left text-xs uppercase tracking-widest text-zinc-500">
                <th className="p-3 font-normal">When</th>
                <th className="p-3 font-normal">Kind</th>
                <th className="p-3 font-normal">Actor</th>
                <th className="p-3 font-normal">Reason</th>
                <th className="p-3 font-normal">Details</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, idx) => (
                <tr
                  key={`${e.created_at}-${idx}`}
                  data-testid="security-event-row"
                  className="border-t border-white/5 hover:bg-white/[0.02]"
                >
                  <td className="p-3 font-mono text-xs text-zinc-500 whitespace-nowrap">
                    {e.created_at
                      ? new Date(e.created_at).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })
                      : '—'}
                  </td>
                  <td className="p-3 text-amber-400 font-mono text-xs">
                    {humanize(e.kind)}
                  </td>
                  <td className="p-3 text-zinc-300 font-mono text-xs">
                    {e.email || (e.user_id ? e.user_id.slice(0, 8) : '—')}
                  </td>
                  <td className="p-3 text-zinc-400">{e.reason || '—'}</td>
                  <td className="p-3 text-zinc-500 font-mono text-xs">
                    {e.details && Object.keys(e.details).length > 0
                      ? Object.entries(e.details)
                          .map(([k, v]) => `${k}=${v}`)
                          .join(' · ')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SecurityEventsPanel;
